<script setup lang="ts">
import {computed} from 'vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import type {VoucherCodeQuery} from '@/ts/types/component/sign-up-type';
import {PARTNER_BRANDING, type PartnerBranding} from '@/ts/utils/partner-branding';

const props = defineProps<{
  partner: VoucherCodeQuery;
}>();

const config = computed((): PartnerBranding | null => PARTNER_BRANDING[props.partner] ?? null);
</script>

<template>
  <div
    v-if="config"
    class="partner-media-panel"
    role="img"
    :aria-label="$t(config.titleKey)"
    :title="$t(config.titleKey)"
    :style="{
      '--partner-media': `url(${config.media})`,
      '--partner-overlay-color': config.overlayColor,
    }"
  >
    <div class="partnership">
      <p class="partnership-text">
        {{ $t(config.captionKey) }}
      </p>
      <svg-illustration
        :name="config.logoAsset"
        class="partner-logo"
        :class="config.panelLogoClass"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.partner-media-panel {
  display: flex;
  // The caption + logo anchor to the bottom of the media at every resolution.
  align-items: flex-end;
  justify-content: center;
  padding: var(--Spacing-spacing-5) var(--Spacing-spacing-4);
  // Partner overlay fading over the photo (per the Figma spec); both layers are
  // identical in light and dark themes.
  background-image:
    linear-gradient(to bottom, transparent 39%, var(--partner-overlay-color) 100%),
    var(--partner-media);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;

  @include screen-lg {
    padding: var(--Spacing-spacing-8) var(--Spacing-spacing-4);
  }
}

.partnership {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-2);
  // Always white: the content sits on the fixed photo + overlay, which are
  // identical in both themes (theme-aware tokens would flip in dark).
  color: #fff;
}

.partnership-text {
  @include font-body-bold;
  margin: 0;
  text-align: center;
}

.partner-logo {
  // Scale the inline SVG to the wrapper width so the visible logo is truly
  // centered under the caption (the atom's default max-width keeps the SVG at
  // its natural size, leaving it left-aligned inside a wider wrapper).
  :deep(svg) {
    width: 100%;
    height: auto;
  }

  &--allianz {
    // 200x52 in the Figma at every resolution (the SVG's own ratio lands at
    // 200x51).
    width: 200px;
  }

  &--usc {
    width: 48px;
  }
}
</style>
